export type Situacion =
  'POR_APROBAR' | 'RECHAZADO' | 'AL_DIA' | 'ATRASADO' | 'CANCELADO' | 'INCUMPLIDO' | 'ANULADO';

export type Servicio = 'SERVICIOS_ESPECIALES' | 'TASA_MENSUAL' | 'ART8';

export const SERVICIOS: { valor: Servicio; etiqueta: string }[] = [
  { valor: 'SERVICIOS_ESPECIALES', etiqueta: 'Servicios especiales' },
  { valor: 'TASA_MENSUAL', etiqueta: 'Tasa mensual' },
  { valor: 'ART8', etiqueta: 'Títulos del Art. 8' },
];

export const CONCEPTOS: Record<Servicio, string> = {
  SERVICIOS_ESPECIALES: 'servicios especiales de recolección y gestión de desechos',
  TASA_MENSUAL: 'la tasa de recolección de residuos sólidos',
  ART8: 'los títulos de crédito del artículo 8 de la ordenanza',
};

export type Garantia = 'LETRA_DE_CAMBIO' | 'GARANTIA_REAL' | 'NINGUNA';

export const GARANTIAS: { valor: Garantia; etiqueta: string }[] = [
  { valor: 'LETRA_DE_CAMBIO', etiqueta: 'Letra de cambio' },
  { valor: 'GARANTIA_REAL', etiqueta: 'Garantía real' },
  { valor: 'NINGUNA', etiqueta: 'Sin garantía' },
];

export interface Cuota {
  numero: number;
  vencimiento: string;
  valor: number;
  pagada: boolean;
  vencida: boolean;
  pagadaEn?: string | null;
  referencia?: string | null;
}

export interface FacturaRecogida {
  id: number;
  historica: boolean;
  numero: string | null;
  periodo: string;
  fechaEmision?: string | null;
  capital: number;
  interes: number;
  mesesDeMora: number;
  total: number;
}

export interface Convenio {
  id: number;
  numero?: string | null;
  clienteId: number;
  cliente: string;
  identificacion: string;
  servicio: Servicio;
  servicioNombre: string;
  fecha?: string | null;
  capital: number;
  interes: number;
  deuda: number;
  entrada: number;
  plazo: number;
  diaDePago: number;
  garantia: Garantia;
  cuotaRedondeada: boolean;
  situacion: Situacion;
  referencia?: string | null;
  observacion?: string | null;
  representante?: string | null;
  concepto?: string | null;
  fechaSolicitud?: string | null;
  sumilla?: string | null;
  migrado: boolean;
  numeroEnElLibro?: number | null;
  recuperadoAntes: number;
  pendiente: number;
  vencido: number;
  cuotasPendientes: number;
  cuotasVencidas: number;
  proximoVencimiento?: string | null;
  proximaCuota?: number | null;
  creadoPor?: string | null;
  creadoEn?: string | null;
  cuotas: Cuota[];
  facturas: FacturaRecogida[];
  /** 2: firmado tras incumplir otro; si este también se incumple, va a coactiva. */
  oportunidad?: number;
  aprobadoPor?: string | null;
  aprobadoEn?: string | null;
  incumplidoEn?: string | null;
}

export type TipoDeDocumento =
  | 'OFICIO'
  | 'CEDULA'
  | 'COMPROBANTE_ENTRADA'
  | 'LETRA_DE_CAMBIO'
  | 'CONVENIO_FIRMADO'
  | 'TITULO_DE_CREDITO'
  | 'OTRO';

export const TIPOS_DE_DOCUMENTO: { valor: TipoDeDocumento; etiqueta: string }[] = [
  { valor: 'CONVENIO_FIRMADO', etiqueta: 'Convenio firmado' },
  { valor: 'OFICIO', etiqueta: 'Oficio de solicitud' },
  { valor: 'CEDULA', etiqueta: 'Cédula o RUC' },
  { valor: 'COMPROBANTE_ENTRADA', etiqueta: 'Comprobante de la entrada' },
  { valor: 'LETRA_DE_CAMBIO', etiqueta: 'Letra de cambio' },
  { valor: 'TITULO_DE_CREDITO', etiqueta: 'Título de crédito' },
  { valor: 'OTRO', etiqueta: 'Otro' },
];

export interface DocumentoDelConvenio {
  id: number;
  tipo: TipoDeDocumento;
  tipoNombre: string;
  nombre: string;
  tipoContenido: string;
  tamanio: number;
  subidoPor: string;
  subidoEn: string;
}

export interface TituloDeCredito {
  id: number;
  numero: string;
  factura: string | null;
  periodo: string | null;
  capital: number;
  interes: number;
  total: number;
  emitidoEl: string;
  emitidoPor: string;
}

export interface Expediente {
  documentos: DocumentoDelConvenio[];
  titulos: TituloDeCredito[];
  facturasSinTitulo: number;
}

export interface Resumen {
  vigentes: number;
  atrasados: number;
  pendiente: number;
  vencido: number;
  porAprobar?: number;
}

export interface FacturaConvenible {
  id: number;
  historica: boolean;
  numero: string | null;
  periodo: string;
  vence: string;
  saldo: number;
  interes: number;
  mesesDeMora: number;
  total: number;
}

export interface Convenible {
  clienteId: number;
  nombre: string;
  identificacion: string;
  /** Las instituciones públicas no firman convenios de pago. */
  institucionPublica?: boolean;
  /** Las reglas de Tesorería, de Configuración: 20 % y 24 meses si no se cambiaron. */
  entradaMinimaPorcentaje?: number;
  plazoMaximo?: number;
  /** Cuántos convenios ha incumplido: con uno, el siguiente es el segundo; con dos, a coactiva. */
  incumplidos?: number;
  facturas: FacturaConvenible[];
}

export interface CuotaSimulada {
  numero: number;
  vencimiento: string;
  valor: number;
}

export const SITUACIONES: Record<Situacion, { rotulo: string; clase: string }> = {
  POR_APROBAR: { rotulo: 'Por aprobar', clase: 'bg-violet-50 text-violet-700 border-violet-200' },
  RECHAZADO: { rotulo: 'Rechazado', clase: 'bg-armazon-100 text-armazon-500 border-armazon-200' },
  AL_DIA: { rotulo: 'Al día', clase: 'bg-marca-50 text-marca-700 border-marca-200' },
  ATRASADO: { rotulo: 'Atrasado', clase: 'bg-amber-50 text-amber-800 border-amber-200' },
  CANCELADO: { rotulo: 'Cancelado', clase: 'bg-sky-50 text-sky-700 border-sky-200' },
  INCUMPLIDO: { rotulo: 'Incumplido', clase: 'bg-red-50 text-red-700 border-red-200' },
  ANULADO: { rotulo: 'Anulado', clase: 'bg-armazon-100 text-armazon-500 border-armazon-200' },
};

export function enDolares(n: number): string {
  return `$${n.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function enFecha(iso?: string | null): string {
  if (!iso) {
    return '—';
  }
  const [anio, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${anio}`;
}
